'use client'

import {useEffect, useState} from 'react'
import {Cpu, Key, Link as LinkIcon, Loader2, Sparkles} from 'lucide-react'
import type {SettingsState} from '@/types'
import {reportTodo} from '@/lib/constants'
import {PageHeader} from '@/components/PageHeader'
import { message } from '@/components/Toast'

export function AiConfigView({
    settings,
    saveSettings,
}: {
    settings: SettingsState
    saveSettings: (patch: Partial<SettingsState>) => void
}) {
    const config = settings.aiConfig
    const [draft, setDraft] = useState(config)
    const [testing, setTesting] = useState(false)
    const [connectErr, setConnectErr] = useState('')

    useEffect(() => {
        setDraft(config)
    }, [config])

    const handleSave = () => {
        saveSettings({aiConfig: draft})
    }

    const testConnectivity = async () => {
        setTesting(true)
        setConnectErr('')
        try {
            const base = (draft.apiBaseUrl || '').replace(/\/$/, '')
            if (!base) throw new Error('请先填写 API Base URL')
            const response = await fetch(`${base}/models`, {
                headers: draft.apiKey ? {Authorization: `Bearer ${draft.apiKey}`} : undefined,
            })
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            // showToast('连通性正常')
            message.success('连通性正常')
        } catch (err) {
            reportTodo('ai-config.test-connectivity')
            setConnectErr(err instanceof Error ? err.message : '连通性测试失败')
        } finally {
            setTesting(false)
        }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader title="AI 大模型配置" description="配置底层对话模型接口，用于驱动 AI 自动回复"/>
            <div className="flex-1 overflow-y-auto p-6">
                <div className="w-full max-w-4xl mr-auto">
                    <div className="bg-white rounded-card shadow-card border border-hair p-8 space-y-6">
                        <div className="mb-2 flex items-center gap-3">
                            <Cpu size={24} className="text-accent"/>
                            <h3 className="text-base font-medium text-ink">接口参数设置</h3>
                        </div>

                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm text-soft">
                                <LinkIcon size={16}/>
                                API Base URL
                            </label>
                            <input
                                type="text"
                                className="input"
                                value={draft.apiBaseUrl}
                                onChange={(event) => setDraft({...draft, apiBaseUrl: event.target.value})}
                                placeholder="https://api.openai.com/v1"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm text-soft">
                                <Key size={16}/>
                                API Key
                            </label>
                            <input
                                type="password"
                                className="input"
                                value={draft.apiKey}
                                onChange={(event) => setDraft({...draft, apiKey: event.target.value})}
                                placeholder="sk-..."
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm text-soft">
                                <Sparkles size={16}/>
                                模型名称
                            </label>
                            <input
                                type="text"
                                className="input"
                                value={draft.model}
                                onChange={(event) => setDraft({...draft, model: event.target.value})}
                                placeholder="gpt-4o-mini"
                            />
                        </div>
                        {connectErr ? (
                            <div className="px-3 py-2 rounded bg-red-50 border border-red-100 text-xs text-red-600">
                                {connectErr}
                            </div>
                        ) : null}

                        <div className="pt-4 border-t border-hair flex items-center justify-end gap-3">
                            <button
                                type="button"
                                data-todo="ai-config.test-connectivity"
                                className="btn-secondary"
                                onClick={testConnectivity}
                                disabled={testing}
                            >
                                {testing ? <Loader2 size={16} className="animate-spin"/> : null}
                                测试连通性
                            </button>
                            <button type="button" className="btn-primary" onClick={handleSave}>
                                保存配置
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
